// Tracker API client — wraps fetch with session_id from localStorage.
const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "tracker_session_v1";

export const trackerSession = {
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  set(session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  },
  clear() {
    localStorage.removeItem(STORAGE_KEY);
  },
};

async function request(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status}: ${text}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function authQS(extra = {}) {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const params = new URLSearchParams({
    email: s.email,
    session_id: s.session_id,
    ...extra,
  });
  return `?${params.toString()}`;
}

function authBody(payload = {}) {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  return { email: s.email, session_id: s.session_id, ...payload };
}

// ---- Auth ----
export const requestOtp = (email) =>
  request("/api/kai/request-code", { method: "POST", body: JSON.stringify({ email }) });

export const verifyOtp = (email, code) =>
  request("/api/kai/verify-code", { method: "POST", body: JSON.stringify({ email, code }) });

export const verifySession = () =>
  request("/api/tracker/auth/verify-session", {
    method: "POST",
    body: JSON.stringify(authBody()),
  });

// ---- Tracker ----
export const getHome = () => request(`/api/tracker/home${authQS()}`);

export const submitCheckin = (payload) =>
  request("/api/tracker/checkin", {
    method: "POST",
    body: JSON.stringify(authBody(payload)),
  });

export const getCheckinToday = () =>
  request(`/api/tracker/checkin/today${authQS()}`);

export const getProgress = (days = 30) =>
  request(`/api/tracker/progress${authQS({ days: String(days) })}`);

export const getKaiContext = () =>
  request(`/api/tracker/kai/context${authQS()}`);

export const logKaiUsage = (role, hadContext, chars) =>
  request("/api/tracker/kai/usage", {
    method: "POST",
    body: JSON.stringify(authBody({ role, had_context: !!hadContext, chars: chars || 0 })),
  });

export const sendKaiMessage = (message) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  // Reuse existing /api/kai/chat which already validates session_id+email.
  return request("/api/kai/chat", {
    method: "POST",
    body: JSON.stringify({
      session_id: s.session_id,
      email: s.email,
      message,
    }),
  });
};

export const getTrackerSubscription = () =>
  request(`/api/tracker/subscription${authQS()}`);

export const startTrackerCheckout = () =>
  request("/api/tracker/checkout", {
    method: "POST",
    body: JSON.stringify(authBody({ origin_url: window.location.origin })),
  });

export const getSymptomTips = (symptom) =>
  request(`/api/tracker/symptom-tips/${encodeURIComponent(symptom)}${authQS()}`);

// ---- Feb 2026 4-feature pack ----

// Feature 1: PK curve (Pro)
export const getPkCurve = (daysBack = 7, daysForward = 7) =>
  request(`/api/tracker/pk-curve${authQS({ days_back: String(daysBack), days_forward: String(daysForward) })}`);

// Feature 2: AI meal photo analysis (multipart)
export const analyzeMeal = async (file) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const form = new FormData();
  form.append("file", file);
  form.append("email", s.email);
  form.append("session_id", s.session_id);
  const res = await fetch(`${API}/api/tracker/analyze-meal`, {
    method: "POST",
    body: form, // Content-Type set automatically (multipart boundary)
  });
  const text = await res.text();
  let data = null;
  try { data = JSON.parse(text); } catch { /* leave null */ }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

// Feature 4: Clinician PDF export — returns a Blob the caller can download
export const exportClinicianPdf = async () => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const res = await fetch(`${API}/api/tracker/export-pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: s.email, session_id: s.session_id }),
  });
  if (!res.ok) {
    let detail = null;
    try { detail = await res.json(); } catch { /* ignore */ }
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.data = detail;
    throw err;
  }
  return res.blob();
};

// V10 — Clinician email delivery (Pro)
export const sendClinicianPdf = (clinicianEmail) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  return request(`/api/tracker/export-pdf/send`, {
    method: "POST",
    body: JSON.stringify({
      email: s.email,
      session_id: s.session_id,
      clinician_email: clinicianEmail,
    }),
  });
};

// V11 — User settings + auto-share-clinic toggle
export const getTrackerSettings = () => request(`/api/tracker/settings${authQS()}`);

export const patchTrackerSettings = (patch) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  return request(`/api/tracker/settings`, {
    method: "PATCH",
    body: JSON.stringify({
      email: s.email,
      session_id: s.session_id,
      ...patch,
    }),
  });
};

// V12 — patient-facing clinician reply banner
export const getClinicianReply = () => request(`/api/tracker/clinician-reply${authQS()}`);

export const markClinicianReplyRead = (token) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  return request(`/api/tracker/clinician-reply/read`, {
    method: "POST",
    body: JSON.stringify({ email: s.email, session_id: s.session_id, token }),
  });
};


// ================================================================
// V14 — API helpers
// ================================================================

// ITEM 1: Quick-Weight widget
export const submitQuickCheckin = (payload) =>
  request(`/api/tracker/quick-checkin`, {
    method: "POST",
    body: JSON.stringify(authBody(payload)),
  });

// ITEM 2: voice, barcode, USDA food search
export const parseVoiceLog = (transcript) =>
  request(`/api/tracker/parse-voice-log`, {
    method: "POST",
    body: JSON.stringify(authBody({ transcript })),
  });

export const lookupBarcode = (barcode) =>
  request(`/api/tracker/lookup-barcode`, {
    method: "POST",
    body: JSON.stringify(authBody({ barcode })),
  });

export const searchFood = (query) =>
  request(`/api/tracker/search-food${authQS({ query })}`);

// ITEM 3: progress photos
export const uploadProgressPhoto = async (file, note = "") => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const fd = new FormData();
  fd.append("file", file);
  fd.append("email", s.email);
  fd.append("session_id", s.session_id);
  fd.append("note", note || "");
  const res = await fetch(`${API}/api/tracker/photos`, { method: "POST", body: fd });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = await res.text().catch(() => "");
    throw err;
  }
  return res.json();
};

export const listProgressPhotos = () =>
  request(`/api/tracker/photos${authQS()}`);

export const deleteProgressPhoto = (publicId) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const params = new URLSearchParams({ email: s.email, session_id: s.session_id });
  return request(
    `/api/tracker/photos/${encodeURIComponent(publicId)}?${params.toString()}`,
    { method: "DELETE" }
  );
};

// ITEM 4: shared quick-weight (Apple Health Shortcut, Google Health Connect, CSV)
export const submitQuickWeight = (weightLbs, source = "manual") =>
  request(`/api/tracker/quick-weight`, {
    method: "POST",
    body: JSON.stringify(authBody({ weight_lbs: Number(weightLbs), source })),
  });

export const importHealthCsv = async (file) => {
  const s = trackerSession.get();
  if (!s) throw new Error("No tracker session");
  const fd = new FormData();
  fd.append("file", file);
  fd.append("email", s.email);
  fd.append("session_id", s.session_id);
  const res = await fetch(`${API}/api/tracker/import-health-csv`, { method: "POST", body: fd });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// ITEM 5: public community count
export const getCommunityCount = async () => {
  const res = await fetch(`${API}/api/tracker/community-count`);
  if (!res.ok) return { community_member_count: 0 };
  return res.json();
};
